import { useEffect, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { VERANSTALTUNG_GRENZEN } from '@mcikm-vote/shared';

import {
  useVeranstaltung,
  useVeranstaltungAnlegen,
  useVeranstaltungBearbeiten,
} from '../../admin/veranstaltungenApi';

interface FormularFelder {
  bezeichnung: string;
  veranstaltungsdatum: string;
  beschreibung: string;
  ort: string;
  interneNotiz: string;
}

const LEER: FormularFelder = {
  bezeichnung: '',
  veranstaltungsdatum: '',
  beschreibung: '',
  ort: '',
  interneNotiz: '',
};

function Feld({
  name,
  beschriftung,
  wert,
  grenze,
  pflicht,
  mehrzeilig,
  typ,
  onAendern,
}: {
  name: string;
  beschriftung: string;
  wert: string;
  grenze?: number;
  pflicht?: boolean;
  mehrzeilig?: boolean;
  typ?: string;
  onAendern: (wert: string) => void;
}): ReactElement {
  const gemeinsam = {
    id: name,
    name,
    value: wert,
    required: pflicht === true,
    ...(grenze === undefined ? {} : { maxLength: grenze }),
    className: 'border-rand bg-hintergrund rounded-md border px-3 py-2',
    onChange: (ereignis: { target: { value: string } }) => {
      onAendern(ereignis.target.value);
    },
  };

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={name} className="text-sm font-medium">
        {beschriftung}
        {pflicht !== true && <span className="text-text-gedaempft"> (optional)</span>}
      </label>
      {mehrzeilig === true ? (
        <textarea {...gemeinsam} rows={4} />
      ) : (
        <input
          {...gemeinsam}
          type={typ ?? 'text'}
          className={`${gemeinsam.className} min-h-touchflaeche`}
        />
      )}
      {grenze !== undefined && (
        <span className="text-text-gedaempft text-xs">
          {wert.length} von {grenze} Zeichen
        </span>
      )}
    </div>
  );
}

/**
 * Anlegen und Bearbeiten einer Veranstaltung (§6.1).
 *
 * Die Feldlängen sind sichtbar, damit eine Ablehnung nicht überrascht. Verbindlich prüft der
 * Server (§25.2); diese Anzeige dient allein der Benutzerführung.
 */
export function VeranstaltungFormularSeite(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const bearbeitet = id !== undefined;
  const navigate = useNavigate();

  const bestehend = useVeranstaltung(id ?? '');
  const anlegen = useVeranstaltungAnlegen();
  const bearbeiten = useVeranstaltungBearbeiten(id ?? '');
  const vorgang = bearbeitet ? bearbeiten : anlegen;

  const [felder, setFelder] = useState<FormularFelder>(LEER);

  useEffect(() => {
    if (bearbeitet && bestehend.data !== undefined) {
      setFelder({
        bezeichnung: bestehend.data.bezeichnung,
        veranstaltungsdatum: bestehend.data.veranstaltungsdatum,
        beschreibung: bestehend.data.beschreibung ?? '',
        ort: bestehend.data.ort ?? '',
        interneNotiz: bestehend.data.interneNotiz ?? '',
      });
    }
  }, [bearbeitet, bestehend.data]);

  function absenden(ereignis: FormEvent<HTMLFormElement>): void {
    ereignis.preventDefault();
    vorgang.mutate(
      {
        bezeichnung: felder.bezeichnung,
        veranstaltungsdatum: felder.veranstaltungsdatum,
        beschreibung: felder.beschreibung === '' ? null : felder.beschreibung,
        ort: felder.ort === '' ? null : felder.ort,
        interneNotiz: felder.interneNotiz === '' ? null : felder.interneNotiz,
      },
      {
        onSuccess: (veranstaltung) => {
          void navigate(`/verwaltung/veranstaltungen/${veranstaltung.id}`);
        },
      },
    );
  }

  if (bearbeitet && bestehend.isPending) {
    return <p className="text-text-gedaempft text-sm">Wird geladen …</p>;
  }

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <h1 className="text-xl font-semibold">
        {bearbeitet ? 'Veranstaltung bearbeiten' : 'Neue Veranstaltung'}
      </h1>

      <form
        onSubmit={absenden}
        className="border-rand bg-flaeche flex flex-col gap-4 rounded-xl border p-4 shadow-sm"
      >
        <Feld
          name="bezeichnung"
          beschriftung="Bezeichnung"
          wert={felder.bezeichnung}
          grenze={VERANSTALTUNG_GRENZEN.bezeichnung}
          pflicht
          onAendern={(wert) => {
            setFelder((alt) => ({ ...alt, bezeichnung: wert }));
          }}
        />

        <Feld
          name="veranstaltungsdatum"
          beschriftung="Veranstaltungsdatum"
          wert={felder.veranstaltungsdatum}
          typ="date"
          pflicht
          onAendern={(wert) => {
            setFelder((alt) => ({ ...alt, veranstaltungsdatum: wert }));
          }}
        />

        <Feld
          name="ort"
          beschriftung="Veranstaltungsort"
          wert={felder.ort}
          grenze={VERANSTALTUNG_GRENZEN.ort}
          onAendern={(wert) => {
            setFelder((alt) => ({ ...alt, ort: wert }));
          }}
        />

        <Feld
          name="beschreibung"
          beschriftung="Beschreibung"
          wert={felder.beschreibung}
          grenze={VERANSTALTUNG_GRENZEN.beschreibung}
          mehrzeilig
          onAendern={(wert) => {
            setFelder((alt) => ({ ...alt, beschreibung: wert }));
          }}
        />

        <div className="flex flex-col gap-1">
          <Feld
            name="interneNotiz"
            beschriftung="Interne Notiz"
            wert={felder.interneNotiz}
            grenze={VERANSTALTUNG_GRENZEN.interneNotiz}
            mehrzeilig
            onAendern={(wert) => {
              setFelder((alt) => ({ ...alt, interneNotiz: wert }));
            }}
          />
          <p className="text-text-gedaempft text-xs">
            Ausschließlich im Administrationsbereich sichtbar. Teilnehmer sehen sie nie.
          </p>
        </div>

        {vorgang.isError && (
          <p role="alert" className="text-fehler text-sm font-medium">
            {vorgang.error.message}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={vorgang.isPending}
            className="bg-akzent text-akzent-text transition hover:brightness-110 min-h-touchflaeche rounded-md px-4 font-medium disabled:pointer-events-none disabled:opacity-60"
          >
            {vorgang.isPending ? 'Wird gespeichert …' : 'Speichern'}
          </button>
          <button
            type="button"
            onClick={() => {
              void navigate(-1);
            }}
            className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand rounded-md border px-4 font-medium"
          >
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  );
}
